import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { useAuth } from "@/contexts/AuthContext";
import {
	Order,
	OrderItem,
	OrderStatus,
	PayoutStatus,
	useOrders,
} from "@/hooks/use-orders";
import { useStripeCheckout } from "@/hooks/use-stripe-checkout";
import { useToast } from "@/hooks/use-toast";
import { formatCurrency } from "@/lib/utils";
import {
	ArrowLeft,
	Calendar,
	CheckCircle2,
	ClipboardList,
	MapPin,
	Package,
	Truck,
	UserCheck,
} from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { OrderStatusUpdate } from "./OrderStatusUpdate";

const getStatusColor = (status: OrderStatus) => {
	switch (status) {
		case "pending":
			return "bg-yellow-100 text-yellow-800";
		case "received":
			return "bg-blue-100 text-blue-800";
		case "processing":
			return "bg-purple-100 text-purple-800";
		case "shipped":
			return "bg-green-100 text-green-800";
		case "delivered":
			return "bg-green-500 text-white";
		case "cancelled":
			return "bg-red-100 text-red-800";
		default:
			return "bg-gray-100 text-gray-800";
	}
};

export function OrderDetail() {
	const { id } = useParams<{ id: string }>();
	const navigate = useNavigate();
	const { isSupplier } = useAuth();
	const { toast } = useToast();
	const {
		fetchOrderById,
		fetchOrderItemsById,
		updateOrderStatus,
		isUpdating,
	} = useOrders();
	const { processPaymentTransfer } = useStripeCheckout();
	const [order, setOrder] = useState<Order | null>(null);
	const [orderItems, setOrderItems] = useState<OrderItem[]>([]);
	const [isLoading, setIsLoading] = useState(true);
	const [isProcessingTransfer, setIsProcessingTransfer] = useState(false);

	// Load order details and items
	useEffect(() => {
		const loadOrderData = async () => {
			if (!id) return;

			try {
				setIsLoading(true);
				const orderData = await fetchOrderById(id);

				if (orderData) {
					// Get order items
					const items = await fetchOrderItemsById(id);

					// Create a properly formatted order object
					const formattedOrder: Order = {
						id: orderData.id,
						user_id: orderData.user_id,
						supplier_id: orderData.supplier_id,
						amount: orderData.amount,
						status: orderData.status as OrderStatus,
						items: items,
						shipping_address: orderData.shipping_address,
						created_at: orderData.created_at,
						updated_at: orderData.updated_at,
						tracking: orderData.tracking?.map((track) => ({
							...track,
							status: track.status as OrderStatus,
						})),
						payment_intent_id: orderData.payment_intent_id,
						payout_status: orderData.payout_status as PayoutStatus,
					};

					setOrder(formattedOrder);
					setOrderItems(items);
				}
			} catch (error) {
				console.error("Error loading order:", error);
			} finally {
				setIsLoading(false);
			}
		};

		loadOrderData();
	}, [id]);

	useEffect(() => {
		const refreshData = async () => {
			if (!id) return;

			const orderData = await fetchOrderById(id);

			if (orderData) {
				// Get order items
				const items = await fetchOrderItemsById(id);

				// Create a properly formatted order object
				const formattedOrder: Order = {
					id: orderData.id,
					user_id: orderData.user_id,
					supplier_id: orderData.supplier_id,
					amount: orderData.amount,
					status: orderData.status as OrderStatus,
					items: items,
					shipping_address: orderData.shipping_address,
					created_at: orderData.created_at,
					updated_at: orderData.updated_at,
					tracking: orderData.tracking?.map((track) => ({
						...track,
						status: track.status as OrderStatus,
					})),
					payment_intent_id: orderData.payment_intent_id,
					payout_status: orderData.payout_status as PayoutStatus,
				};

				setOrder(formattedOrder);
				setOrderItems(items);
			}
		};

		refreshData();
	}, [isUpdating]);

	const handleAcceptOrder = async () => {
		if (!order || !order.payment_intent_id) {
			toast({
				title: "Error",
				description:
					"Cannot process this order. Missing payment information.",
				variant: "destructive",
			});
			return;
		}

		setIsProcessingTransfer(true);

		try {
			// Process the transfer for accepted order
			const result = await processPaymentTransfer(
				order.payment_intent_id
			);

			if (result) {
				toast({
					title: "Order Accepted",
					description:
						"Order has been accepted and payment processed.",
				});

				// First update the order status to 'received'
				await updateOrderStatus({
					orderId: order.id,
					status: "received",
					notes: "Order accepted by supplier",
				});

				// Refresh order data
				const updatedOrder = await fetchOrderById(order.id);
				if (updatedOrder) {
					setOrder({
						...order,
						status: "received" as OrderStatus,
						payout_status:
							updatedOrder.payout_status as PayoutStatus,
					});
				}
			} else {
				toast({
					title: "Order Accepted",
					description:
						"Order accepted, but there was an issue processing payment. It will be handled automatically.",
				});
			}
		} catch (error) {
			console.error("Error accepting order:", error);
			toast({
				title: "Error",
				description: "Failed to accept order. Please try again.",
				variant: "destructive",
			});
		} finally {
			setIsProcessingTransfer(false);
		}
	};

	const handleRejectOrder = async () => {
		try {
			await updateOrderStatus({
				orderId: order!.id,
				status: "cancelled",
				notes: "Order rejected by supplier",
			});

			toast({
				title: "Order Rejected",
				description: "Order has been rejected and marked as cancelled.",
			});
		} catch (error) {
			console.error("Error rejecting order:", error);
			toast({
				title: "Error",
				description: "Failed to reject order. Please try again.",
				variant: "destructive",
			});
		}
	};

	if (isLoading) {
		return (
			<div className="py-10 text-center">Loading order details...</div>
		);
	}

	if (!order) {
		return (
			<div className="py-10 text-center">
				<p className="text-gray-500">Order not found.</p>
				<Button
					className="mt-4"
					onClick={() => navigate("/dashboard/orders")}>
					<ArrowLeft className="mr-2 h-4 w-4" />
					Back to Orders
				</Button>
			</div>
		);
	}

	// Get the latest tracking info
	const latestTracking =
		order.tracking && order.tracking.length > 0
			? [...order.tracking].sort(
					(a, b) =>
						new Date(b.created_at).getTime() -
						new Date(a.created_at).getTime()
			  )[0]
			: null;

	return (
		<div className="space-y-6">
			<div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
				<div className="space-y-1">
					<Button
						variant="ghost"
						onClick={() => navigate("/dashboard/orders")}
						className="pl-0 hover:bg-transparent hover:text-gray-500 p-0 mb-2">
						<ArrowLeft className="mr-2 h-4 w-4" />
						Back to Orders
					</Button>
					<h1 className="text-2xl font-semibold tracking-tight">
						Order #{order.id.substring(0, 8)}
					</h1>
					<div className="flex items-center gap-2">
						<Calendar className="h-4 w-4 text-gray-500" />
						<p className="text-gray-500">
							{new Date(order.created_at).toLocaleDateString()} |{" "}
							{new Date(order.created_at).toLocaleTimeString()}
						</p>
					</div>
				</div>
				<div className="mt-4 sm:mt-0 flex flex-col items-end gap-2">
					<Badge
						className={`${getStatusColor(
							order.status as OrderStatus
						)} px-3 py-1 text-sm`}>
						{order.status}
					</Badge>

					{order.payout_status && (
						<span className="text-xs text-gray-500">
							Payment Status: {order.payout_status}
						</span>
					)}
				</div>
			</div>

			<div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
				<div className="lg:col-span-2 space-y-6">
					<Card>
						<CardHeader>
							<CardTitle>Order Items</CardTitle>
							<CardDescription>
								{orderItems.length} item
								{orderItems.length !== 1 ? "s" : ""}
							</CardDescription>
						</CardHeader>
						<CardContent>
							<div className="space-y-4">
								{orderItems.map((item) => (
									<div key={item.id} className="flex gap-4">
										{item.image ? (
											<div className="h-16 w-16 rounded overflow-hidden bg-gray-100 flex-shrink-0">
												<img
													src={item.image}
													alt={item.name}
													className="h-full w-full object-cover"
												/>
											</div>
										) : (
											<div className="h-16 w-16 rounded bg-gray-100 flex items-center justify-center flex-shrink-0">
												<Package className="h-8 w-8 text-gray-400" />
											</div>
										)}
										<div className="flex-1">
											<h4 className="font-medium">
												{item.name}
											</h4>
											<div className="text-sm text-gray-500">
												{formatCurrency(item.price)} ×{" "}
												{item.quantity}
												{item.caseQuantity &&
												item.caseQuantity > 1 ? (
													<span className="ml-1">
														({item.quantity} ×{" "}
														{item.caseQuantity}{" "}
														units/case ={" "}
														{item.quantity *
															item.caseQuantity}{" "}
														units)
													</span>
												) : null}
											</div>
										</div>
										<div className="font-medium">
											{formatCurrency(
												item.price * item.quantity
											)}
										</div>
									</div>
								))}

								<Separator className="my-4" />

								<div className="space-y-1">
									<div className="flex justify-between">
										<span className="text-gray-500">
											Subtotal
										</span>
										<span>
											{formatCurrency(order.amount)}
										</span>
									</div>
									<div className="flex justify-between">
										<span className="text-gray-500">
											Shipping
										</span>
										<span>Free</span>
									</div>
									<Separator className="my-2" />
									<div className="flex justify-between font-medium">
										<span>Total</span>
										<span>
											{formatCurrency(order.amount)}
										</span>
									</div>
								</div>
							</div>
						</CardContent>
					</Card>

					{isSupplier && (
						<Card>
							<CardHeader>
								<CardTitle>Update Order Status</CardTitle>
								<CardDescription>
									{order.status === "pending"
										? "Accept this order to begin processing and receive payment."
										: "Change the status of this order and provide tracking information."}
								</CardDescription>
							</CardHeader>
							<CardContent>
								<OrderStatusUpdate
									orderId={order.id}
									currentStatus={order.status as OrderStatus}
									onStatusUpdate={(data) =>
										updateOrderStatus(data)
									}
									onAcceptOrder={
										order.status === "pending"
											? handleAcceptOrder
											: undefined
									}
									onRejectOrder={
										order.status === "pending"
											? handleRejectOrder
											: undefined
									}
									isUpdating={
										isUpdating || isProcessingTransfer
									}
								/>
							</CardContent>
						</Card>
					)}

					{order.tracking && order.tracking.length > 0 && (
						<Card>
							<CardHeader>
								<CardTitle>Tracking History</CardTitle>
							</CardHeader>
							<CardContent>
								<div className="space-y-4">
									{[...order.tracking]
										.sort(
											(a, b) =>
												new Date(
													b.created_at
												).getTime() -
												new Date(a.created_at).getTime()
										)
										.map((tracking, index) => (
											<div
												key={index}
												className="border-l-2 border-gray-200 pl-4 pb-4">
												<div className="flex items-start">
													<div className="absolute -ml-6 mt-0.5 h-4 w-4 rounded-full bg-white border-2 border-gray-300"></div>
													<div className="flex-1">
														<div className="flex items-center gap-2 mb-1">
															<Badge
																className={getStatusColor(
																	tracking.status
																)}>
																{
																	tracking.status
																}
															</Badge>
															<span className="text-sm text-gray-500">
																{new Date(
																	tracking.created_at
																).toLocaleDateString()}{" "}
																{new Date(
																	tracking.created_at
																).toLocaleTimeString()}
															</span>
														</div>

														{tracking.tracking_number && (
															<div className="flex items-center text-sm text-gray-600 mb-1">
																<Truck className="h-4 w-4 mr-1" />
																Tracking:{" "}
																{
																	tracking.tracking_number
																}
																{tracking.carrier &&
																	` (${tracking.carrier})`}
															</div>
														)}

														{tracking.notes && (
															<p className="text-sm text-gray-600 mt-1">
																{tracking.notes}
															</p>
														)}
													</div>
												</div>
											</div>
										))}
								</div>
							</CardContent>
						</Card>
					)}
				</div>

				<div className="space-y-6">
					<Card>
						<CardHeader>
							<CardTitle>Customer Information</CardTitle>
						</CardHeader>
						<CardContent>
							<div className="space-y-4">
								<div>
									<h4 className="text-sm font-medium text-gray-500 mb-1">
										Contact Details
									</h4>
									<p className="font-medium">
										{order.shipping_address.firstName}{" "}
										{order.shipping_address.lastName}
									</p>
									<p className="text-gray-600">
										{order.shipping_address.email}
									</p>
									<p className="text-gray-600">
										{order.shipping_address.phone}
									</p>
								</div>

								<div>
									<h4 className="text-sm font-medium text-gray-500 mb-1">
										<MapPin className="h-4 w-4 inline mr-1" />
										Shipping Address
									</h4>
									<p className="text-gray-600">
										{order.shipping_address.address}
										<br />
										{order.shipping_address.city},{" "}
										{order.shipping_address.postalCode}
										<br />
										{order.shipping_address.country || ""}
									</p>
								</div>
							</div>
						</CardContent>
					</Card>

					{latestTracking && latestTracking.status === "shipped" && (
						<Card>
							<CardHeader>
								<CardTitle>Shipping Information</CardTitle>
							</CardHeader>
							<CardContent>
								<div className="space-y-2">
									<div className="flex items-center gap-2">
										<Truck className="h-5 w-5 text-gray-500" />
										<div>
											<p className="font-medium">
												Tracking Number:{" "}
												{latestTracking.tracking_number}
											</p>
											{latestTracking.carrier && (
												<p className="text-sm text-gray-500">
													Carrier:{" "}
													{latestTracking.carrier}
												</p>
											)}
										</div>
									</div>

									{latestTracking.notes && (
										<div className="mt-2 text-sm">
											<p className="text-gray-500">
												{latestTracking.notes}
											</p>
										</div>
									)}
								</div>
							</CardContent>
						</Card>
					)}

					<Card>
						<CardHeader>
							<CardTitle>Order Status</CardTitle>
						</CardHeader>
						<CardContent>
							<div className="space-y-4">
								<div className="flex items-center gap-2">
									<div
										className={`h-8 w-8 rounded-full flex items-center justify-center ${
											order.status === "pending" ||
											order.status === "received" ||
											order.status === "processing" ||
											order.status === "shipped" ||
											order.status === "delivered"
												? "bg-green-100 text-green-600"
												: "bg-gray-100 text-gray-400"
										}`}>
										<CheckCircle2 className="h-5 w-5" />
									</div>
									<div>
										<p className="font-medium">
											Order Placed
										</p>
										<p className="text-xs text-gray-500">
											{new Date(
												order.created_at
											).toLocaleDateString()}
										</p>
									</div>
								</div>

								<div className="flex items-center gap-2">
									<div
										className={`h-8 w-8 rounded-full flex items-center justify-center ${
											order.status === "received" ||
											order.status === "processing" ||
											order.status === "shipped" ||
											order.status === "delivered"
												? "bg-green-100 text-green-600"
												: "bg-gray-100 text-gray-400"
										}`}>
										<UserCheck className="h-5 w-5" />
									</div>
									<div>
										<p className="font-medium">
											Order Received
										</p>
										<p className="text-xs text-gray-500">
											{order.status === "pending"
												? "Pending"
												: "Confirmed by seller"}
										</p>
									</div>
								</div>

								<div className="flex items-center gap-2">
									<div
										className={`h-8 w-8 rounded-full flex items-center justify-center ${
											order.status === "processing" ||
											order.status === "shipped" ||
											order.status === "delivered"
												? "bg-green-100 text-green-600"
												: "bg-gray-100 text-gray-400"
										}`}>
										<Package className="h-5 w-5" />
									</div>
									<div>
										<p className="font-medium">
											Processing
										</p>
										<p className="text-xs text-gray-500">
											{order.status === "pending" ||
											order.status === "received"
												? "Waiting"
												: "Your order is being prepared"}
										</p>
									</div>
								</div>

								<div className="flex items-center gap-2">
									<div
										className={`h-8 w-8 rounded-full flex items-center justify-center ${
											order.status === "shipped" ||
											order.status === "delivered"
												? "bg-green-100 text-green-600"
												: "bg-gray-100 text-gray-400"
										}`}>
										<Truck className="h-5 w-5" />
									</div>
									<div>
										<p className="font-medium">Shipped</p>
										<p className="text-xs text-gray-500">
											{order.status === "shipped" ||
											order.status === "delivered"
												? latestTracking?.tracking_number
													? `Tracking: ${latestTracking.tracking_number}`
													: "On the way"
												: "Not shipped yet"}
										</p>
									</div>
								</div>

								<div className="flex items-center gap-2">
									<div
										className={`h-8 w-8 rounded-full flex items-center justify-center ${
											order.status === "delivered"
												? "bg-green-100 text-green-600"
												: "bg-gray-100 text-gray-400"
										}`}>
										<ClipboardList className="h-5 w-5" />
									</div>
									<div>
										<p className="font-medium">Delivered</p>
										<p className="text-xs text-gray-500">
											{order.status === "delivered"
												? "Order completed"
												: "Pending delivery"}
										</p>
									</div>
								</div>
							</div>
						</CardContent>
					</Card>
				</div>
			</div>
		</div>
	);
}
